import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    getCheapestPriceFromCurrentSearch,
    getCheapestPriceShownInDeparture,
    getExpensivePriceShownInDeparture,
    getCheapestPriceShownInReturn,
    getExpensivePriceShownInReturn,
    getNumberOfDepDaysWithFares,
    getNumberOfRetDaysWithFares,
    getMonthlyDaysWithFaresDeparture,
    getMonthlyDaysWithFaresReturn,
    getDepartureFare,
    getReturnFare,
    getCheapestDepartureDaysList,
    getCheapestReturnDaysList,
} from '../../../redux/flexibleDates/selectors';
import { TrackingUtils } from '../../../utils/TrackingUtils';
import { SearchProp } from '../../../models/Search';
import { TripType } from '../../../models/index';
import { EntryPointProp } from '../../../models/EntryPoint';
import { withPageContext } from '../../../utils/PageContext';
import { ABUtils, DateUtils } from '../../../utils';

const trackFareEventsDeparture = ({
    action,
    type,
    tripType,
    isCheapest,
    cheapestPrice,
    cheapestPriceShownDeparture,
    expensivePriceShownDeparture,
    numberOfDepDaysWithFares,
    monthlyDaysWithFaresDeparture,
    minMonthSeen,
    maxMonthSeen,
    departureFare,
    trackEvent,
}) => {
    const prices = {
        cheapest: cheapestPrice,
        isCheapest: isCheapest,
        cheapestPriceShown: cheapestPriceShownDeparture,
        expensivePriceShown: expensivePriceShownDeparture,
        numberOfDaysWithFares: numberOfDepDaysWithFares,
        monthlyDaysWithFares: monthlyDaysWithFaresDeparture,
        minMonthSeen: minMonthSeen,
        maxMonthSeen: maxMonthSeen,
        selectedDayFare: departureFare,
    };
    const faresInCalendarLabel = TrackingUtils.LABEL.faresInCalendarLabel({
        type,
        calendar: 'dep',
        tripType,
        faresInfo: prices,
    });
    trackEvent({ action, label: faresInCalendarLabel });
};

const trackFareEventsReturn = ({
    action,
    type,
    tripType,
    cheapestPrice,
    isCheapest,
    cheapestPriceShownReturn,
    expensivePriceShownReturn,
    numberOfRetDaysWithFares,
    monthlyDaysWithFaresReturn,
    minMonthSeen,
    maxMonthSeen,
    returnFare,
    trackEvent,
}) => {
    const prices = {
        cheapest: cheapestPrice,
        isCheapest: isCheapest,
        cheapestPriceShown: cheapestPriceShownReturn,
        expensivePriceShown: expensivePriceShownReturn,
        numberOfDaysWithFares: numberOfRetDaysWithFares,
        monthlyDaysWithFares: monthlyDaysWithFaresReturn,
        minMonthSeen: minMonthSeen,
        maxMonthSeen: maxMonthSeen,
        selectedDayFare: returnFare,
    };
    const faresInCalendarLabelRT = TrackingUtils.LABEL.faresInCalendarLabel({
        type,
        calendar: 'ret',
        tripType,
        faresInfo: prices,
    });
    trackEvent({ action, label: faresInCalendarLabelRT });
};

const trackFareEventsByTrackType = ({
    tripType,
    departureDate,
    returnDate,
    action,
    type,
    cheapestDepartureDaysList,
    cheapestReturnDaysList,
    cheapestPrice,
    cheapestPriceShownDeparture,
    expensivePriceShownDeparture,
    numberOfDepDaysWithFares,
    monthlyDaysWithFaresDeparture,
    monthlyDaysWithFaresReturn,
    minMonthSeen,
    maxMonthSeen,
    departureFare,
    cheapestPriceShownReturn,
    expensivePriceShownReturn,
    numberOfRetDaysWithFares,
    returnFare,
    trackEvent,
}) => {
    if ((TripType.ONE_WAY === tripType || !departureDate || 'continue_calendar_dep' === type) && 'continue_calendar_ret' !== type) {
        trackFareEventsDeparture({
            action,
            type,
            tripType,
            isCheapest: cheapestDepartureDaysList.indexOf(DateUtils.toString(departureDate)) > -1,
            cheapestPrice,
            cheapestPriceShownDeparture,
            expensivePriceShownDeparture,
            numberOfDepDaysWithFares,
            monthlyDaysWithFaresDeparture,
            minMonthSeen,
            maxMonthSeen,
            departureFare,
            trackEvent,
        });
    } else {
        trackFareEventsReturn({
            action,
            type,
            tripType,
            isCheapest: cheapestReturnDaysList.indexOf(DateUtils.toString(returnDate)) > -1,
            cheapestPrice,
            cheapestPriceShownReturn,
            expensivePriceShownReturn,
            numberOfRetDaysWithFares,
            monthlyDaysWithFaresReturn,
            minMonthSeen,
            maxMonthSeen,
            returnFare,
            trackEvent,
        });
    }
};

export const trackFareEvents = ({
    hasFaresInCalendar,
    currentSearch,
    activeProduct,
    cheapestPrice,
    cheapestDepartureDaysList,
    cheapestReturnDaysList,
    cheapestPriceShownDeparture,
    expensivePriceShownDeparture,
    cheapestPriceShownReturn,
    expensivePriceShownReturn,
    numberOfDepDaysWithFares,
    numberOfRetDaysWithFares,
    monthlyDaysWithFaresDeparture,
    monthlyDaysWithFaresReturn,
    minMonthSeen,
    maxMonthSeen,
    departureFare,
    returnFare,
    trackEvent,
    trackType,
}) => {
    const { tripType, segments, isHotel } = currentSearch;
    const isNotMultiple = tripType === TripType.ROUND_TRIP || (tripType === TripType.ONE_WAY && segments.length === 1);
    const depDate = segments[0].date;
    const retDate = segments[1] ? segments[1].date : false;

    if (isNotMultiple && !isHotel && hasFaresInCalendar) {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        if (trackType) {
            trackFareEventsByTrackType({
                action,
                type: trackType,
                tripType,
                departureDate: depDate,
                returnDate: retDate,
                cheapestDepartureDaysList,
                cheapestReturnDaysList,
                cheapestPrice,
                cheapestPriceShownReturn,
                expensivePriceShownReturn,
                numberOfRetDaysWithFares,
                returnFare,
                cheapestPriceShownDeparture,
                expensivePriceShownDeparture,
                numberOfDepDaysWithFares,
                monthlyDaysWithFaresDeparture,
                monthlyDaysWithFaresReturn,
                minMonthSeen,
                maxMonthSeen,
                departureFare,
                trackEvent,
            });
        } else {
            const type =
                window.location.hash.indexOf('flights/edit') > -1
                    ? 'change_dates'
                    : 'select_calendar';
            trackFareEventsDeparture({
                action,
                type,
                tripType,
                isCheapest: cheapestDepartureDaysList.indexOf(DateUtils.toString(depDate)) > -1,
                cheapestPrice,
                cheapestPriceShownDeparture,
                expensivePriceShownDeparture,
                numberOfDepDaysWithFares,
                departureFare,
                trackEvent,
            });
            if (tripType === TripType.ROUND_TRIP) {
                trackFareEventsReturn({
                    action,
                    type,
                    tripType,
                    isCheapest: cheapestReturnDaysList.indexOf(DateUtils.toString(retDate)) > -1,
                    cheapestPrice,
                    cheapestPriceShownReturn,
                    expensivePriceShownReturn,
                    numberOfRetDaysWithFares,
                    returnFare,
                    trackEvent,
                });
            }
        }
    }
};

export const FaresTrackingCalculator = ({
    activeProduct,
    children,
    currentSearch,
    trackEvent,
    cheapestDepartureDaysList,
    cheapestReturnDaysList,
    cheapestPrice,
    cheapestPriceShownDeparture,
    expensivePriceShownDeparture,
    cheapestPriceShownReturn,
    expensivePriceShownReturn,
    numberOfDepDaysWithFares,
    numberOfRetDaysWithFares,
    monthlyDaysWithFaresDeparture,
    monthlyDaysWithFaresReturn,
    minMonthSeen,
    maxMonthSeen,
    departureFare,
    returnFare,
    extraTracking,
    trackType,
}) => {
    const { isPricesInCalendarActive: hasFaresInCalendar } = ABUtils.getABs();
    const _extraTracking = () => {
        trackFareEvents({
            hasFaresInCalendar,
            currentSearch,
            activeProduct,
            cheapestPrice,
            cheapestPriceShownDeparture,
            expensivePriceShownDeparture,
            cheapestPriceShownReturn,
            expensivePriceShownReturn,
            numberOfDepDaysWithFares,
            numberOfRetDaysWithFares,
            monthlyDaysWithFaresDeparture,
            monthlyDaysWithFaresReturn,
            minMonthSeen,
            maxMonthSeen,
            departureFare,
            returnFare,
            trackEvent,
            trackType,
            cheapestDepartureDaysList,
            cheapestReturnDaysList,
        });
        extraTracking();
    };
    return children({ extraTracking: _extraTracking });
};

FaresTrackingCalculator.propTypes = {
    activeProduct: EntryPointProp.isRequired,
    currentSearch: SearchProp.isRequired,
    trackEvent: PropTypes.func.isRequired,
    cheapestPrice: PropTypes.number.isRequired,
    cheapestPriceShownDeparture: PropTypes.number.isRequired,
    expensivePriceShownDeparture: PropTypes.number.isRequired,
    cheapestPriceShownReturn: PropTypes.number.isRequired,
    expensivePriceShownReturn: PropTypes.number.isRequired,
    numberOfDepDaysWithFares: PropTypes.number.isRequired,
    numberOfRetDaysWithFares: PropTypes.number.isRequired,
    monthlyDaysWithFaresDeparture: PropTypes.object.isRequired,
    monthlyDaysWithFaresReturn: PropTypes.object.isRequired,
    cheapestDepartureDaysList: PropTypes.array.isRequired,
    cheapestReturnDaysList: PropTypes.array.isRequired,
    departureFare: PropTypes.number.isRequired,
    returnFare: PropTypes.number.isRequired,
    extraTracking: PropTypes.func,
    trackType: PropTypes.string,
    minMonthSeen: PropTypes.object,
    maxMonthSeen: PropTypes.object,
};

FaresTrackingCalculator.defaultProps = {
    extraTracking: () => {},
    trackType: '',
};

const mapStateToProps = (state) => ({
    activeProduct: state.activeProduct.activeProduct,
    currentSearch: state.currentSearch,
    cheapestPrice: getCheapestPriceFromCurrentSearch(state),
    cheapestPriceShownDeparture: getCheapestPriceShownInDeparture(state),
    expensivePriceShownDeparture: getExpensivePriceShownInDeparture(state),
    cheapestPriceShownReturn: getCheapestPriceShownInReturn(state),
    expensivePriceShownReturn: getExpensivePriceShownInReturn(state),
    numberOfDepDaysWithFares: getNumberOfDepDaysWithFares(state),
    numberOfRetDaysWithFares: getNumberOfRetDaysWithFares(state),
    monthlyDaysWithFaresDeparture: getMonthlyDaysWithFaresDeparture(state),
    monthlyDaysWithFaresReturn: getMonthlyDaysWithFaresReturn(state),
    departureFare: getDepartureFare(state),
    returnFare: getReturnFare(state),
    cheapestDepartureDaysList: getCheapestDepartureDaysList(state),
    cheapestReturnDaysList: getCheapestReturnDaysList(state),
});

export const ConnectedFaresTrackingCalculator = connect(
    mapStateToProps,
    {},
)(withPageContext(FaresTrackingCalculator));
